import { useTranslation } from 'react-i18next';
import { Store } from 'lucide-react';

import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { formatDate } from '@/lib/format';

import { MerchantPayoutAddressesDialog } from './MerchantPayoutAddressesDialog';
import { MerchantShareDialog } from './MerchantShareDialog';
import { formatPercent } from './format';
import { configuredPayoutAddressKinds } from './payout-address';
import type { LinkStatus, PlatformMerchant } from './types';

type Props = {
  merchants: PlatformMerchant[];
  /** The platform default, passed down to the share dialog's hint. */
  defaultSharePercent: number | null;
};

const STATUS_VARIANT: Record<LinkStatus, BadgeVariant> = {
  provisioning: 'yellow',
  active: 'green',
  disabled: 'secondary',
  failed: 'destructive',
};

/** The networks the merchant's checkout offers right now (read-only: the merchant picks them in the CRM). */
function OfferedNetworks({ merchant }: { merchant: PlatformMerchant }) {
  const { t } = useTranslation('manageCryptoPayments');
  if (merchant.offeredNetworks === null) {
    return <span className="text-muted-foreground text-xs">{t('merchants.networksUnknown')}</span>;
  }
  return (
    <div className="space-y-1">
      {merchant.sandbox === true && <Badge variant="yellow">{t('merchants.sandbox')}</Badge>}
      {merchant.offeredNetworks.length === 0 ? (
        <span className="text-muted-foreground block text-xs">{t('merchants.noNetworks')}</span>
      ) : (
        <ul className="space-y-0.5 text-xs">
          {merchant.offeredNetworks.map((network) => (
            <li key={network.chainId}>
              <span className="font-medium">{network.name}</span>{' '}
              <span className="text-muted-foreground">{network.assets.join(', ')}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Every store with crypto payments enabled, with the share actually applied
 * at settlement and where its funds go. The rows mirror crypto-api; the two
 * dialogs write through the platform API and refresh the list. Payout
 * addresses decide where a merchant's funds land, so that dialog is offered
 * to superadmins only (the server's `superadminProcedure` refuses anyone
 * else); other staff read the chains and see why they cannot edit.
 */
export function MerchantsTable({ merchants, defaultSharePercent }: Props) {
  const { t, i18n } = useTranslation('manageCryptoPayments');
  const { isSuperAdmin } = useSessionClaims();

  if (merchants.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Store />
          </EmptyMedia>
          <EmptyTitle>{t('merchants.empty')}</EmptyTitle>
          <EmptyDescription>{t('merchants.emptyDescription')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('merchants.columns.store')}</TableHead>
          <TableHead>{t('merchants.columns.account')}</TableHead>
          <TableHead>{t('merchants.columns.status')}</TableHead>
          <TableHead>{t('merchants.columns.share')}</TableHead>
          <TableHead>{t('merchants.columns.payoutAddresses')}</TableHead>
          <TableHead>{t('merchants.columns.networks')}</TableHead>
          <TableHead>{t('merchants.columns.enabledAt')}</TableHead>
          <TableHead className="text-right">{t('merchants.columns.actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {merchants.map((merchant) => {
          const hasOverride = merchant.sharePercentOverride !== null;
          const configuredKinds = configuredPayoutAddressKinds(merchant.payoutAddresses);
          return (
            <TableRow key={merchant.storeId}>
              <TableCell className="align-top">
                <div className="font-medium">{merchant.storeName}</div>
                <div className="text-muted-foreground break-all font-mono text-xs">{merchant.storeId}</div>
              </TableCell>

              <TableCell className="align-top">
                <div className="text-sm">{merchant.accountName ?? merchant.accountId}</div>
                {merchant.accountName && (
                  <div className="text-muted-foreground break-all font-mono text-xs">
                    {merchant.accountId}
                  </div>
                )}
              </TableCell>

              <TableCell className="align-top">
                <Badge variant={STATUS_VARIANT[merchant.status]}>
                  {t(`merchants.statuses.${merchant.status}`)}
                </Badge>
                {merchant.stats && (
                  <div className="text-muted-foreground mt-1 text-xs">
                    {t('merchants.paidSessions', { count: merchant.stats.paidSessions })}
                  </div>
                )}
              </TableCell>

              <TableCell className="align-top">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium tabular-nums">
                    {formatPercent(merchant.effectiveSharePercent, i18n.language)}
                  </span>
                  <Badge variant={hasOverride ? 'blue' : 'outline'}>
                    {hasOverride ? t('merchants.override') : t('merchants.default')}
                  </Badge>
                </div>
              </TableCell>

              <TableCell className="align-top">
                {configuredKinds.length === 0 ? (
                  <span className="text-muted-foreground text-xs">{t('merchants.noAddresses')}</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {configuredKinds.map((kind) => (
                      <Badge key={kind} variant="gray">
                        {t(`chains.${kind}`)}
                      </Badge>
                    ))}
                  </div>
                )}
              </TableCell>

              <TableCell className="align-top">
                <OfferedNetworks merchant={merchant} />
              </TableCell>

              <TableCell className="whitespace-nowrap align-top text-sm">
                {merchant.enabledAt ? (
                  formatDate(merchant.enabledAt)
                ) : (
                  <span className="text-muted-foreground">{t('merchants.notEnabled')}</span>
                )}
              </TableCell>

              <TableCell className="align-top">
                <div className="flex flex-wrap items-center justify-end gap-1.5">
                  <MerchantShareDialog merchant={merchant} defaultSharePercent={defaultSharePercent} />
                  {isSuperAdmin ? (
                    <MerchantPayoutAddressesDialog merchant={merchant} />
                  ) : (
                    <span className="text-muted-foreground max-w-[18ch] text-right text-xs">
                      {t('merchants.payoutAddressesSuperadminOnly')}
                    </span>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
